import { useState, useEffect, useCallback } from 'react';
import { collection, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { safeStorageGet, safeStorageRemove, safeStorageSet } from '../utils/safeStorage';

interface PendingOperation {
  id: string;
  type: 'add' | 'update' | 'delete';
  collection: string;
  docId?: string;
  data?: any;
  timestamp: number;
}

const PENDING_OPS_KEY = 'aficionado_pending_operations';
const OFFLINE_DATA_KEY = 'aficionado_offline_data';
const MAX_PENDING_OPERATIONS = 250;
const MAX_CACHE_ENTRIES = 200;

const readJsonFromStorage = <T>(key: string, fallback: T): T => {
  try {
    const raw = safeStorageGet(key);
    if (!raw) return fallback;
    return JSON.parse(raw) as T;
  } catch (e) {
    console.error(`Failed to read localStorage key "${key}":`, e);
    return fallback;
  }
};

const writeJsonToStorage = (key: string, value: unknown): void => {
  try {
    safeStorageSet(key, JSON.stringify(value));
  } catch (e) {
    console.error(`Failed to write localStorage key "${key}":`, e);
  }
};

export interface OfflineSyncState {
  isOnline: boolean;
  pendingCount: number;
  isSyncing: boolean;
  lastSyncTime: Date | null;
}

export const useOfflineSync = (userId?: string) => {
  const [isOnline, setIsOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);
  const [pendingOperations, setPendingOperations] = useState<PendingOperation[]>([]);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState<Date | null>(null);

  // Load pending operations from localStorage
  useEffect(() => {
    const stored = readJsonFromStorage<PendingOperation[]>(PENDING_OPS_KEY, []);
    if (!Array.isArray(stored)) return;
    setPendingOperations(stored.slice(-MAX_PENDING_OPERATIONS));
  }, []);

  // Save pending operations to localStorage
  useEffect(() => {
    writeJsonToStorage(PENDING_OPS_KEY, pendingOperations);
  }, [pendingOperations]);

  // Monitor online status
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
    };

    const handleOffline = () => {
      setIsOnline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const syncPendingOperations = useCallback(async () => {
    if (!isOnline || pendingOperations.length === 0) return;

    setIsSyncing(true);

    const successfulIds: string[] = [];

    for (const op of pendingOperations) {
      try {
        switch (op.type) {
          case 'add':
            await addDoc(collection(db, op.collection), op.data);
            successfulIds.push(op.id);
            break;
          case 'update':
            if (op.docId) {
              await updateDoc(doc(db, op.collection, op.docId), op.data);
              successfulIds.push(op.id);
            }
            break;
          case 'delete':
            if (op.docId) {
              await deleteDoc(doc(db, op.collection, op.docId));
              successfulIds.push(op.id);
            }
            break;
        }
      } catch (error) {
        console.error(`Failed to sync operation ${op.id}:`, error);
      }
    }

    setPendingOperations(prev => prev.filter(op => !successfulIds.includes(op.id)));
    setIsSyncing(false);
    setLastSyncTime(new Date());
  }, [isOnline, pendingOperations]);

  // Auto-sync when coming back online
  useEffect(() => {
    if (isOnline && pendingOperations.length > 0 && userId) {
      syncPendingOperations();
    }
  }, [isOnline, pendingOperations.length, syncPendingOperations, userId]);

  const queueOperation = useCallback((
    type: 'add' | 'update' | 'delete',
    collectionName: string,
    data?: any,
    docId?: string
  ): string => {
    const opId = `op_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    const operation: PendingOperation = {
      id: opId,
      type,
      collection: collectionName,
      docId,
      data,
      timestamp: Date.now(),
    };

    setPendingOperations(prev => {
      const next = [...prev, operation];
      return next.length > MAX_PENDING_OPERATIONS ? next.slice(next.length - MAX_PENDING_OPERATIONS) : next;
    });

    return opId;
  }, []);

  // Cache data locally for offline access
  const cacheData = useCallback((key: string, data: any) => {
    try {
      const cache = readJsonFromStorage<Record<string, { data: any; timestamp: number }>>(OFFLINE_DATA_KEY, {});
      cache[key] = {
        data,
        timestamp: Date.now(),
      };

      const entries = Object.entries(cache);
      if (entries.length > MAX_CACHE_ENTRIES) {
        entries
          .sort((a, b) => a[1].timestamp - b[1].timestamp)
          .slice(0, entries.length - MAX_CACHE_ENTRIES)
          .forEach(([staleKey]) => {
            delete cache[staleKey];
          });
      }
      writeJsonToStorage(OFFLINE_DATA_KEY, cache);
    } catch (e) {
      console.error('Failed to cache data:', e);
    }
  }, []);

  const getCachedData = useCallback((key: string): any | null => {
    try {
      const cache = readJsonFromStorage<Record<string, { data: any; timestamp: number }>>(OFFLINE_DATA_KEY, {});
      const cached = cache[key];
      if (cached) {
        // Check if cache is older than 24 hours
        const age = Date.now() - cached.timestamp;
        if (age < 24 * 60 * 60 * 1000) {
          return cached.data;
        }
      }
      return null;
    } catch (e) {
      console.error('Failed to get cached data:', e);
      return null;
    }
  }, []);

  const clearCache = useCallback(() => {
    try {
      safeStorageRemove(OFFLINE_DATA_KEY);
      safeStorageRemove(PENDING_OPS_KEY);
    } catch (e) {
      console.error('Failed to clear local cache:', e);
    }
    setPendingOperations([]);
  }, []);

  // Wrapper for Firestore operations with offline support
  const addDocument = useCallback(async (collectionName: string, data: any) => {
    if (isOnline) {
      try {
        const docRef = await addDoc(collection(db, collectionName), data);
        return { success: true, id: docRef.id };
      } catch (error) {
        // Fall back to offline queue
        const opId = queueOperation('add', collectionName, data);
        return { success: true, id: opId, queued: true };
      }
    } else {
      const opId = queueOperation('add', collectionName, data);
      return { success: true, id: opId, queued: true };
    }
  }, [isOnline, queueOperation]);

  const updateDocument = useCallback(async (collectionName: string, docId: string, data: any) => {
    if (isOnline) {
      try {
        await updateDoc(doc(db, collectionName, docId), data);
        return { success: true };
      } catch (error) {
        queueOperation('update', collectionName, data, docId);
        return { success: true, queued: true };
      }
    } else {
      queueOperation('update', collectionName, data, docId);
      return { success: true, queued: true };
    }
  }, [isOnline, queueOperation]);

  const deleteDocument = useCallback(async (collectionName: string, docId: string) => {
    if (isOnline) {
      try {
        await deleteDoc(doc(db, collectionName, docId));
        return { success: true };
      } catch (error) {
        queueOperation('delete', collectionName, undefined, docId);
        return { success: true, queued: true };
      }
    } else {
      queueOperation('delete', collectionName, undefined, docId);
      return { success: true, queued: true };
    }
  }, [isOnline, queueOperation]);

  return {
    isOnline,
    pendingCount: pendingOperations.length,
    isSyncing,
    lastSyncTime,
    syncPendingOperations,
    addDocument,
    updateDocument,
    deleteDocument,
    cacheData,
    getCachedData,
    clearCache,
  };
};
